import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AccountActions } from './account.actions';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { AccountService } from './account.service';
import { ExternalNavigationService } from '@letly/services';

@Injectable()
export class AccountEffects {
  private actions$ = inject(Actions);
  private accountService = inject(AccountService);
  private messageService = inject(MessageService);
  private externalNavigation = inject(ExternalNavigationService);

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadUser),
      switchMap(() =>
        this.accountService.getUserData().pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.loadUserSuccess({ user: res.data });
            }
            return AccountActions.loadUserFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.loadUserFailure({ error: error.message })))
        )
      )
    )
  );

  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadCountries),
      switchMap(() =>
        this.accountService.getCountries().pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.loadCountriesSuccess({ countries: res.data.items });
            }
            return AccountActions.loadCountriesFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.loadCountriesFailure({ error: error.message })))
        )
      )
    )
  );

  loadCountryTinRequirements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadCountryTinRequirements),
      switchMap(() =>
        this.accountService.getCountryTinRequirements().pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.loadCountryTinRequirementsSuccess({ requirements: res.data.items });
            }
            return AccountActions.loadCountryTinRequirementsFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.loadCountryTinRequirementsFailure({ error: error.message })))
        )
      )
    )
  );

  startLandlordIdv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startLandlordIdv),
      switchMap(() =>
        this.accountService.startIdv$().pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.startLandlordIdvSuccess({ verification: res.data });
            }
            return AccountActions.startLandlordIdvFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.startLandlordIdvFailure({ error: error.message })))
        )
      )
    )
  );

  startLandlordIdvSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startLandlordIdvSuccess),
      tap(({ verification }) => {
        if (verification.clientLink?.url) {
          this.externalNavigation.navigate(verification.clientLink.url);
        }
      }),
      map(() => AccountActions.loadUser())
    )
  );

  submitLandlordClaims$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.submitLandlordClaims),
      switchMap(({ claims }) =>
        this.accountService.startLandlordIdvVerification(claims).pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.submitLandlordClaimsSuccess({ verification: res.data });
            }
            return AccountActions.submitLandlordClaimsFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.submitLandlordClaimsFailure({ error: error.message })))
        )
      )
    )
  );

  submitLandlordClaimsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.submitLandlordClaimsSuccess),
      switchMap(({ verification }) =>
        this.accountService.initiateVeriffLink(verification).pipe(
          map(() => AccountActions.loadUser()),
          catchError(() => of(AccountActions.loadUser()))
        )
      )
    )
  );

  startRightToRent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startRightToRent),
      switchMap(({ subtype }) =>
        this.accountService.rightToRentVerify$(subtype).pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.startRightToRentSuccess({ verification: res.data });
            }
            return AccountActions.startRightToRentFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.startRightToRentFailure({ error: error.message })))
        )
      )
    )
  );

  startRightToRentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startRightToRentSuccess),
      tap(({ verification }) => {
        this.messageService.showSnackbar($localize`:@@account.toast.verification-started:Verification started`, ToastSeverity.Success);
        if (verification.clientLink?.url) {
          this.externalNavigation.navigate(verification.clientLink.url);
        }
      }),
      map(() => AccountActions.loadUser())
    )
  );

  startIncomeVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startIncomeVerification),
      switchMap(() =>
        this.accountService.startIncomeVerification().pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.startIncomeVerificationSuccess({ verification: res.data });
            }
            return AccountActions.startIncomeVerificationFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.startIncomeVerificationFailure({ error: error.message })))
        )
      )
    )
  );

  startIncomeVerificationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startIncomeVerificationSuccess),
      switchMap(({ verification }) => {
        if (verification.clientLink?.token) {
          return this.accountService.initiatePlaidLink(verification).pipe(
            map(() => AccountActions.loadUser()),
            catchError(() => of(AccountActions.loadUser()))
          );
        }
        return of(AccountActions.loadUser());
      })
    )
  );

  skipIncomeVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.skipIncomeVerification),
      switchMap(({ skipReason, skipReasonText }) =>
        this.accountService.skipIncomeVerification(skipReason, skipReasonText).pipe(
          map((res) => res.success
            ? AccountActions.skipIncomeVerificationSuccess({ verification: res.data })
            : AccountActions.skipIncomeVerificationFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.skipIncomeVerificationFailure({ error: error.message })))
        )
      )
    )
  );

  startCreditVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startCreditVerification),
      switchMap(({ addressHistories }) =>
        this.accountService.startCreditVerification(addressHistories).pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.startCreditVerificationSuccess({ verification: res.data });
            }
            return AccountActions.startCreditVerificationFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.startCreditVerificationFailure({ error: error.message })))
        )
      )
    )
  );

  startCreditVerificationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startCreditVerificationSuccess),
      tap(() => {
        this.messageService.showSnackbar($localize`:@@account.toast.credit-verification-started:Credit check submitted.`, ToastSeverity.Success);
      }),
      map(() => AccountActions.loadUser())
    )
  );

  skipCreditVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.skipCreditVerification),
      switchMap(({ skipReason, skipReasonText }) =>
        this.accountService.skipCreditVerification(skipReason, skipReasonText).pipe(
          map((res) => res.success
            ? AccountActions.skipCreditVerificationSuccess({ verification: res.data })
            : AccountActions.skipCreditVerificationFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.skipCreditVerificationFailure({ error: error.message })))
        )
      )
    )
  );

  startEmploymentReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startEmploymentReference),
      switchMap(({ formClaims }) =>
        this.accountService.startEmploymentReferenceVerification(formClaims).pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.startEmploymentReferenceSuccess({ verification: res.data });
            }
            return AccountActions.startEmploymentReferenceFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.startEmploymentReferenceFailure({ error: error.message })))
        )
      )
    )
  );

  startEmploymentReferenceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startEmploymentReferenceSuccess),
      tap(() => {
        this.messageService.showSnackbar($localize`:@@account.toast.employment-reference-started:Employment reference submitted.`, ToastSeverity.Success);
      }),
      map(() => AccountActions.loadUser())
    )
  );

  skipEmploymentReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.skipEmploymentReference),
      switchMap(({ skipReason, skipReasonText }) =>
        this.accountService.skipEmploymentReferenceVerification(skipReason, skipReasonText).pipe(
          map((res) => res.success
            ? AccountActions.skipEmploymentReferenceSuccess({ verification: res.data })
            : AccountActions.skipEmploymentReferenceFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.skipEmploymentReferenceFailure({ error: error.message })))
        )
      )
    )
  );

  startLandlordReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startLandlordReference),
      switchMap(({ formClaims }) =>
        this.accountService.startLandlordReferenceVerification(formClaims).pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.startLandlordReferenceSuccess({ verification: res.data });
            }
            return AccountActions.startLandlordReferenceFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.startLandlordReferenceFailure({ error: error.message })))
        )
      )
    )
  );

  startLandlordReferenceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startLandlordReferenceSuccess),
      tap(() => {
        this.messageService.showSnackbar($localize`:@@account.toast.landlord-reference-started:Landlord reference submitted.`, ToastSeverity.Success);
      }),
      map(() => AccountActions.loadUser())
    )
  );

  skipLandlordReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.skipLandlordReference),
      switchMap(({ skipReason, skipReasonText }) =>
        this.accountService.skipLandlordReferenceVerification(skipReason, skipReasonText).pipe(
          map((res) => res.success
            ? AccountActions.skipLandlordReferenceSuccess({ verification: res.data })
            : AccountActions.skipLandlordReferenceFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.skipLandlordReferenceFailure({ error: error.message })))
        )
      )
    )
  );

  skipIdv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.skipIdv),
      switchMap(({ skipReason, skipReasonText }) =>
        this.accountService.skipIdvVerification(skipReason, skipReasonText).pipe(
          map((res) => res.success
            ? AccountActions.skipIdvSuccess({ verification: res.data })
            : AccountActions.skipIdvFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.skipIdvFailure({ error: error.message })))
        )
      )
    )
  );

  skipLandlordIdv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.skipLandlordIdv),
      switchMap(({ skipReason, skipReasonText }) =>
        this.accountService.skipLandlordIdvVerification(skipReason, skipReasonText).pipe(
          map((res) => res.success
            ? AccountActions.skipLandlordIdvSuccess({ verification: res.data })
            : AccountActions.skipLandlordIdvFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.skipLandlordIdvFailure({ error: error.message })))
        )
      )
    )
  );

  skipVerificationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountActions.skipIncomeVerificationSuccess,
        AccountActions.skipCreditVerificationSuccess,
        AccountActions.skipEmploymentReferenceSuccess,
        AccountActions.skipLandlordReferenceSuccess,
        AccountActions.skipIdvSuccess,
        AccountActions.skipLandlordIdvSuccess
      ),
      tap(() => {
        this.messageService.showSnackbar($localize`:@@account.toast.verification-skipped:Verification skipped`, ToastSeverity.Success);
      }),
      map(() => AccountActions.loadUser())
    )
  );

  loadIdvCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadIdvCheckDetail),
      switchMap(() =>
        this.accountService.getIdvCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadIdvCheckDetailSuccess({ check: res.data })
            : AccountActions.loadIdvCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadIdvCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  loadLandlordIdvCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadLandlordIdvCheckDetail),
      switchMap(() =>
        this.accountService.getLandlordIdvCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadLandlordIdvCheckDetailSuccess({ check: res.data })
            : AccountActions.loadLandlordIdvCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadLandlordIdvCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  loadRightToRentCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadRightToRentCheckDetail),
      switchMap(() =>
        this.accountService.getRightToRentCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadRightToRentCheckDetailSuccess({ check: res.data })
            : AccountActions.loadRightToRentCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadRightToRentCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  loadCreditCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadCreditCheckDetail),
      switchMap(() =>
        this.accountService.getCreditCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadCreditCheckDetailSuccess({ check: res.data })
            : AccountActions.loadCreditCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadCreditCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  loadIncomeCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadIncomeCheckDetail),
      switchMap(() =>
        this.accountService.getIncomeCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadIncomeCheckDetailSuccess({ check: res.data })
            : AccountActions.loadIncomeCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadIncomeCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  loadEmploymentReferenceCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadEmploymentReferenceCheckDetail),
      switchMap(() =>
        this.accountService.getEmploymentReferenceCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadEmploymentReferenceCheckDetailSuccess({ check: res.data })
            : AccountActions.loadEmploymentReferenceCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadEmploymentReferenceCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  loadLandlordReferenceCheckDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadLandlordReferenceCheckDetail),
      switchMap(() =>
        this.accountService.getLandlordReferenceCheck().pipe(
          map((res) => res.success
            ? AccountActions.loadLandlordReferenceCheckDetailSuccess({ check: res.data })
            : AccountActions.loadLandlordReferenceCheckDetailFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.loadLandlordReferenceCheckDetailFailure({ error: error.message })))
        )
      )
    )
  );

  updateCreditNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateCreditNotes),
      switchMap(({ notes }) =>
        this.accountService.updateCreditNotes(notes).pipe(
          map((res) => res.success
            ? AccountActions.updateCreditNotesSuccess({ check: res.data })
            : AccountActions.updateCreditNotesFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.updateCreditNotesFailure({ error: error.message })))
        )
      )
    )
  );

  updateIncomeNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateIncomeNotes),
      switchMap(({ notes }) =>
        this.accountService.updateIncomeNotes(notes).pipe(
          map((res) => res.success
            ? AccountActions.updateIncomeNotesSuccess({ check: res.data })
            : AccountActions.updateIncomeNotesFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.updateIncomeNotesFailure({ error: error.message })))
        )
      )
    )
  );

  updateEmploymentReferenceNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateEmploymentReferenceNotes),
      switchMap(({ notes }) =>
        this.accountService.updateEmploymentReferenceNotes(notes).pipe(
          map((res) => res.success
            ? AccountActions.updateEmploymentReferenceNotesSuccess({ check: res.data })
            : AccountActions.updateEmploymentReferenceNotesFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.updateEmploymentReferenceNotesFailure({ error: error.message })))
        )
      )
    )
  );

  updateLandlordReferenceNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateLandlordReferenceNotes),
      switchMap(({ notes }) =>
        this.accountService.updateLandlordReferenceNotes(notes).pipe(
          map((res) => res.success
            ? AccountActions.updateLandlordReferenceNotesSuccess({ check: res.data })
            : AccountActions.updateLandlordReferenceNotesFailure({ error: res.niceErrorMessage })),
          catchError((error) => of(AccountActions.updateLandlordReferenceNotesFailure({ error: error.message })))
        )
      )
    )
  );

  updateNotesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AccountActions.updateCreditNotesSuccess,
          AccountActions.updateIncomeNotesSuccess,
          AccountActions.updateEmploymentReferenceNotesSuccess,
          AccountActions.updateLandlordReferenceNotesSuccess
        ),
        tap(() => {
          this.messageService.showSnackbar($localize`:@@account.toast.note-saved:Note saved`, ToastSeverity.Success);
        })
      ),
    { dispatch: false }
  );

  updateUserName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateUserName),
      switchMap(({ firstName, lastName }) =>
        this.accountService.updateUserName(firstName, lastName).pipe(
          map((res) => {
            if (res.success) {
              return AccountActions.updateUserNameSuccess({ user: res.data });
            }
            return AccountActions.updateUserNameFailure({ error: res.niceErrorMessage });
          }),
          catchError((error) => of(AccountActions.updateUserNameFailure({ error: error.message })))
        )
      )
    )
  );

  updateUserNameSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.updateUserNameSuccess),
        tap(() => {
          this.messageService.showSnackbar($localize`:@@account.toast.name-updated:Name updated`, ToastSeverity.Success);
        })
      ),
    { dispatch: false }
  );

  showError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AccountActions.loadUserFailure,
          AccountActions.loadCountriesFailure,
          AccountActions.loadCountryTinRequirementsFailure,
          AccountActions.startLandlordIdvFailure,
          AccountActions.skipIdvFailure,
          AccountActions.skipLandlordIdvFailure,
          AccountActions.submitLandlordClaimsFailure,
          AccountActions.startRightToRentFailure,
          AccountActions.startIncomeVerificationFailure,
          AccountActions.skipIncomeVerificationFailure,
          AccountActions.startCreditVerificationFailure,
          AccountActions.skipCreditVerificationFailure,
          AccountActions.startEmploymentReferenceFailure,
          AccountActions.skipEmploymentReferenceFailure,
          AccountActions.startLandlordReferenceFailure,
          AccountActions.skipLandlordReferenceFailure,
          AccountActions.updateCreditNotesFailure,
          AccountActions.updateIncomeNotesFailure,
          AccountActions.updateEmploymentReferenceNotesFailure,
          AccountActions.updateLandlordReferenceNotesFailure,
          AccountActions.updateUserNameFailure,
          AccountActions.loadIdvCheckDetailFailure,
          AccountActions.loadLandlordIdvCheckDetailFailure,
          AccountActions.loadRightToRentCheckDetailFailure,
          AccountActions.loadCreditCheckDetailFailure,
          AccountActions.loadIncomeCheckDetailFailure,
          AccountActions.loadEmploymentReferenceCheckDetailFailure,
          AccountActions.loadLandlordReferenceCheckDetailFailure
        ),
        tap(({ error }) => {
          this.messageService.showSnackbar(error, ToastSeverity.Error);
        })
      ),
    { dispatch: false }
  );

  refetchIdvDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startLandlordIdvSuccess, AccountActions.skipIdvSuccess),
      map(() => AccountActions.loadIdvCheckDetail())
    )
  );

  refetchLandlordIdvDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.submitLandlordClaimsSuccess, AccountActions.skipLandlordIdvSuccess),
      map(() => AccountActions.loadLandlordIdvCheckDetail())
    )
  );

  refetchRightToRentDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startRightToRentSuccess),
      map(() => AccountActions.loadRightToRentCheckDetail())
    )
  );

  refetchIncomeDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startIncomeVerificationSuccess, AccountActions.skipIncomeVerificationSuccess),
      map(() => AccountActions.loadIncomeCheckDetail())
    )
  );

  refetchCreditDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startCreditVerificationSuccess, AccountActions.skipCreditVerificationSuccess),
      map(() => AccountActions.loadCreditCheckDetail())
    )
  );

  refetchEmploymentDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startEmploymentReferenceSuccess, AccountActions.skipEmploymentReferenceSuccess),
      map(() => AccountActions.loadEmploymentReferenceCheckDetail())
    )
  );

  refetchLandlordReferenceDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.startLandlordReferenceSuccess, AccountActions.skipLandlordReferenceSuccess),
      map(() => AccountActions.loadLandlordReferenceCheckDetail())
    )
  );
}
